import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus, Filter, Download, Eye, Edit, Trash2, Search, Calendar,
    Package, Truck, MapPin, Clock, CheckCircle, AlertTriangle,
    Map, User, Building2, DollarSign, MoreVertical, RefreshCw,
    ArrowUpDown, SortAsc, SortDesc, FileText, Star, Flag,
    Activity, Users, Container, Settings, Phone, Shield,
    Camera, Thermometer, Navigation, Bell, Globe, Target,
    Factory, Ship, Warehouse, Timer, Hash, ArrowLeft
} from 'lucide-react';

const JobsPage = () => {
    // State management
    const [loading, setLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [sortField, setSortField] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        type: '',
        priority: '',
        customer: '',
        transporter: '',
        container: '',
        vehicle: '',
        dateFrom: '',
        dateTo: '',
        detentionRisk: '',
        trackingStatus: ''
    });

    // Enhanced mock data with leg concept matching your Create Jobs Page structure
    const mockJobs = [
        {
            id: 'JOB001',
            jobNumber: 'TT-2025-001',
            customerPO: 'PO-TATA-2025-0156',
            description: 'Electronics shipment from Mumbai to Delhi warehouse',
            type: 'import',
            status: 'in_transit',
            priority: 'high',
            customer: {
                id: 'CUST001',
                name: 'Tata Motors Ltd',
                contact: 'Rajesh Kumar',
                phone: '+91-9876543210',
                email: 'rajesh.kumar@tatamotors.com',
                address: 'Plot 15, Industrial Area, Pune - 411026'
            },
            vendor: {
                id: 'VEND001',
                name: 'Mumbai Port Trust',
                contact: 'Mahesh Patil',
                phone: '+91-9876543220',
                type: 'Port Authority'
            },
            transporter: {
                id: 'TRANS001',
                name: 'Blue Dart Express Ltd',
                code: 'BDE',
                contact: 'Priya Singh',
                phone: '+91-9876543230',
                rating: 4.8,
                totalJobs: 1247,
                onTimeDelivery: 94.2
            },
            container: {
                id: 'CONT001',
                number: 'MSKU-123456-7',
                type: '20ft',
                owner: 'Maersk Line',
                tare: 2200,
                maxWeight: 24000,
                status: 'Available',
                location: 'Mumbai Port',
                freeTime: 72,
                usedTime: 15,
                detentionRate: 500,
                trackingEnabled: true,
                temperature: 25,
                humidity: 65,
                lastTempReading: '2025-06-07T10:30:00'
            },
            legs: [
                {
                    id: 1,
                    expanded: false,
                    type: 'pickup',
                    customer: 'CUST001',
                    vendor: 'VEND001',
                    port: 'PORT001',
                    warehouse: 'WH001',
                    transporter: 'TRANS001',
                    vehicle: 'VEH001',
                    driver: 'DRV001',
                    origin: 'Mumbai Port Trust, Gate No. 4, Mumbai - 400001',
                    destination: 'Delhi North Warehouse, Sector 15, Gurgaon - 122001',
                    vehicleType: 'container_truck',
                    goodsType: 'electronics',
                    container: 'CONT001',
                    distance: 1424,
                    estimatedHours: 36,
                    actualHours: 34,
                    journeyDate: '2025-06-05',
                    journeyTime: '08:00',
                    temperatureMonitoring: false,
                    temperatureRange: '-5 to 25°C',
                    gpsTracking: true,
                    photoUpdates: true,
                    geofencing: true,
                    trackingRequired: true,
                    specialInstructions: 'Handle with care - fragile electronics',
                    status: 'completed',
                    progress: 100,
                    startTime: '2025-06-05T08:00:00',
                    endTime: '2025-06-06T18:00:00'
                },
                {
                    id: 2,
                    expanded: false,
                    type: 'delivery',
                    customer: 'CUST001',
                    vendor: 'VEND001',
                    port: '',
                    warehouse: 'WH001',
                    transporter: 'TRANS001',
                    vehicle: 'VEH002',
                    driver: 'DRV002',
                    origin: 'Delhi North Warehouse, Sector 15, Gurgaon - 122001',
                    destination: 'Tata Motors Plant, Aurangabad - 431005',
                    vehicleType: 'container_truck',
                    goodsType: 'electronics',
                    container: 'CONT001',
                    distance: 450,
                    estimatedHours: 12,
                    actualHours: null,
                    journeyDate: '2025-06-07',
                    journeyTime: '06:00',
                    temperatureMonitoring: false,
                    temperatureRange: '-5 to 25°C',
                    gpsTracking: true,
                    photoUpdates: true,
                    geofencing: true,
                    trackingRequired: true,
                    specialInstructions: 'Final delivery to customer facility',
                    status: 'in_transit',
                    progress: 65,
                    startTime: '2025-06-07T06:00:00',
                    endTime: null,
                    estimatedArrival: '2025-06-07T18:00:00'
                }
            ],
            route: {
                totalDistance: 1874,
                estimatedDuration: 48,
                actualDuration: 34,
                currentLeg: 2,
                efficiency: 92.5
            },
            tracking: {
                gpsEnabled: true,
                temperatureMonitoring: false,
                photoUpdates: true,
                realTimeAlerts: true,
                geofencing: true,
                provider: 'TruckGuru',
                lastUpdate: '2025-06-07T10:30:00',
                currentLocation: {
                    lat: 18.5204,
                    lng: 73.8567,
                    address: 'Highway NH4, Near Aurangabad, Maharashtra',
                    landmark: '2 km before Tata Motors Plant'
                },
                speed: 65,
                eta: '2025-06-07T18:00:00'
            },
            timeline: {
                pickupDate: '2025-06-05',
                pickupTime: '08:00',
                deliveryDate: '2025-06-07',
                deliveryTime: '18:00',
                actualPickup: '2025-06-05T08:00:00',
                estimatedDelivery: '2025-06-07T18:00:00'
            },
            pricing: {
                estimatedCost: 25620,
                quotedPrice: 25000,
                actualCost: 24500,
                fuelSurcharge: 1200,
                otherCharges: 500,
                tollCharges: 800,
                driverAllowance: 1000,
                paymentTerms: 'credit_15',
                advanceAmount: 10000,
                balanceAmount: 14500
            },
            alerts: [
                {
                    id: 1,
                    type: 'warning',
                    severity: 'medium',
                    message: 'Route deviation detected - driver took alternate route due to traffic',
                    timestamp: '2025-06-07T10:15:00',
                    resolved: false,
                    category: 'route'
                }
            ],
            detentionRisk: 'low',
            rating: 4.5,
            createdAt: '2025-06-05T08:00:00',
            updatedAt: '2025-06-07T10:30:00',
            specialInstructions: 'Handle with care - fragile electronics. Contact customer 1 hour before delivery.',
            kpis: {
                onTimeDeliveryScore: 95,
                costEfficiency: 87,
                customerSatisfaction: 4.5,
                complianceScore: 98
            }
        },
        {
            id: 'JOB002',
            jobNumber: 'TT-2025-002',
            customerPO: 'PO-MARUTI-2025-0089',
            description: 'Automotive parts export to Chennai Port',
            type: 'export',
            status: 'loading',
            priority: 'medium',
            customer: {
                id: 'CUST002',
                name: 'Maruti Suzuki India Ltd',
                contact: 'Priya Sharma',
                phone: '+91-9876543211',
                email: 'priya.sharma@maruti.com',
                address: 'Maruti Suzuki Plant, Gurgaon - 122015'
            },
            vendor: {
                id: 'VEND002',
                name: 'Chennai Logistics Pvt Ltd',
                contact: 'Lakshmi Menon',
                phone: '+91-9876543221',
                type: 'Logistics Provider'
            },
            transporter: {
                id: 'TRANS002',
                name: 'Gati Ltd',
                code: 'GTI',
                contact: 'Ramesh Gupta',
                phone: '+91-9876543231',
                rating: 4.6,
                totalJobs: 892,
                onTimeDelivery: 91.8
            },
            container: {
                id: 'CONT002',
                number: 'MSCU-789012-3',
                type: '40ft',
                owner: 'MSC',
                tare: 3800,
                maxWeight: 30480,
                status: 'Available',
                location: 'Chennai Port',
                freeTime: 96,
                usedTime: 8,
                detentionRate: 750,
                trackingEnabled: true,
                temperature: 22,
                humidity: 58,
                lastTempReading: '2025-06-07T10:45:00'
            },
            legs: [
                {
                    id: 1,
                    type: 'pickup',
                    origin: 'Maruti Suzuki Plant, Gurgaon - 122015',
                    destination: 'Delhi Warehouse, Sector 15, Gurgaon - 122001',
                    distance: 25,
                    estimatedHours: 1.5,
                    actualHours: null,
                    vehicle: {
                        number: 'DL-02-XY-9876',
                        type: 'Ashok Leyland 2518',
                        capacity: '40T'
                    },
                    driver: {
                        name: 'Suresh Yadav',
                        phone: '+91-9876543241',
                        license: 'DL-0720110012346',
                        experience: '12 years',
                        rating: 4.6
                    },
                    status: 'in_progress',
                    progress: 15,
                    startTime: '2025-06-07T08:00:00',
                    estimatedArrival: '2025-06-07T09:30:00'
                }
            ],
            route: {
                totalDistance: 25,
                estimatedDuration: 1.5,
                actualDuration: null,
                currentLeg: 1,
                efficiency: null
            },
            tracking: {
                gpsEnabled: true,
                temperatureMonitoring: false,
                photoUpdates: true,
                realTimeAlerts: true,
                geofencing: true,
                provider: 'Fleet Edge',
                lastUpdate: '2025-06-07T10:45:00',
                currentLocation: {
                    lat: 28.6139,
                    lng: 77.2090,
                    address: 'Maruti Suzuki Plant, Gurgaon',
                    landmark: 'Near Plant Gate 2'
                },
                speed: 0,
                eta: '2025-06-07T09:30:00'
            },
            alerts: [],
            detentionRisk: 'medium',
            rating: 4.2,
            createdAt: '2025-06-07T08:00:00',
            updatedAt: '2025-06-07T10:45:00'
        },
        {
            id: 'JOB003',
            jobNumber: 'TT-2025-003',
            customerPO: 'PO-INFOSYS-2025-0234',
            description: 'IT equipment domestic transfer',
            type: 'domestic',
            status: 'delayed',
            priority: 'high',
            customer: {
                id: 'CUST003',
                name: 'Infosys Limited',
                contact: 'Amit Singh',
                phone: '+91-9876543212',
                email: 'amit.singh@infosys.com',
                address: 'Electronic City Phase 1, Bangalore - 560100'
            },
            vendor: {
                id: 'VEND003',
                name: 'Delhi Transport Co',
                contact: 'Harinder Singh',
                phone: '+91-9876543222',
                type: 'Transport Company'
            },
            transporter: {
                id: 'TRANS003',
                name: 'TCI Express Ltd',
                code: 'TCI',
                contact: 'Lakshmi Devi',
                phone: '+91-9876543232',
                rating: 4.7,
                totalJobs: 654,
                onTimeDelivery: 87.3
            },
            container: {
                id: 'CONT003',
                number: 'CMAU-345678-9',
                type: '20ft',
                owner: 'CMA CGM',
                tare: 2180,
                maxWeight: 24000,
                status: 'In Use',
                location: 'Kandla Port',
                freeTime: 72,
                usedTime: 68,
                detentionRate: 450,
                trackingEnabled: true,
                temperature: 28,
                humidity: 72,
                lastTempReading: '2025-06-07T11:00:00'
            },
            legs: [
                {
                    id: 1,
                    type: 'pickup',
                    origin: 'Chennai Port, North Port Area, Chennai - 600001',
                    destination: 'Transit Hub, Highway Transit Hub, Vellore - 632014',
                    distance: 145,
                    estimatedHours: 3.5,
                    actualHours: 4.2,
                    status: 'completed',
                    progress: 100
                },
                {
                    id: 2,
                    type: 'delivery',
                    origin: 'Transit Hub, Highway Transit Hub, Vellore - 632014',
                    destination: 'Electronic City Phase 1, Bangalore - 560100',
                    distance: 200,
                    estimatedHours: 4.0,
                    actualHours: null,
                    status: 'delayed',
                    progress: 45,
                    estimatedArrival: '2025-06-07T22:00:00'
                }
            ],
            route: {
                totalDistance: 345,
                estimatedDuration: 7.5,
                actualDuration: null,
                currentLeg: 2,
                efficiency: 78.2
            },
            tracking: {
                gpsEnabled: true,
                temperatureMonitoring: true,
                photoUpdates: true,
                realTimeAlerts: true,
                geofencing: true,
                provider: 'LogiTrack'
            },
            alerts: [
                {
                    id: 1,
                    type: 'danger',
                    message: 'Delivery delayed by 2 hours',
                    timestamp: '2025-06-07T11:00:00'
                },
                {
                    id: 2,
                    type: 'warning',
                    message: 'Detention time approaching limit',
                    timestamp: '2025-06-07T09:30:00'
                }
            ],
            detentionRisk: 'high',
            rating: 3.8,
            createdAt: '2025-06-06T06:00:00',
            updatedAt: '2025-06-07T11:00:00'
        },
        {
            id: 'JOB004',
            jobNumber: 'TT-2025-004',
            customerPO: 'PO-COAL-2025-0067',
            description: 'Coal shipment delivery completed',
            type: 'import',
            status: 'completed',
            priority: 'low',
            customer: {
                id: 'CUST004',
                name: 'Coal India Ltd',
                contact: 'Sunita Reddy',
                phone: '+91-9876543213',
                email: 'sunita.reddy@coalindia.in',
                address: 'Coal Bhawan, Lodhi Road, New Delhi - 110003'
            },
            vendor: {
                id: 'VEND004',
                name: 'Gujarat Logistics Hub',
                contact: 'Kiran Patel',
                phone: '+91-9876543223',
                type: 'Warehouse Provider'
            },
            transporter: {
                id: 'TRANS004',
                name: 'VRL Logistics',
                code: 'VRL',
                contact: 'Sunita Reddy',
                phone: '+91-9876543233',
                rating: 4.5,
                totalJobs: 1456,
                onTimeDelivery: 96.1
            },
            container: {
                id: 'CONT004',
                number: 'COSU-901234-5',
                type: '40hq',
                owner: 'COSCO',
                tare: 4200,
                maxWeight: 30480,
                status: 'Available',
                location: 'Mumbai Port',
                freeTime: 120,
                usedTime: 52,
                detentionRate: 800,
                trackingEnabled: true,
                temperature: 24,
                humidity: 60
            },
            legs: [
                {
                    id: 1,
                    type: 'pickup',
                    origin: 'Kolkata Port, Netaji Subhash Dock, Kolkata - 700001',
                    destination: 'Coal India Warehouse, Durgapur - 713201',
                    distance: 420,
                    estimatedHours: 8.5,
                    actualHours: 8.0,
                    status: 'completed',
                    progress: 100
                }
            ],
            route: {
                totalDistance: 420,
                estimatedDuration: 8.5,
                actualDuration: 8.0,
                currentLeg: 1,
                efficiency: 106.25
            },
            tracking: {
                gpsEnabled: true,
                temperatureMonitoring: false,
                photoUpdates: true,
                realTimeAlerts: true,
                geofencing: true,
                provider: 'TrackIt'
            },
            alerts: [],
            detentionRisk: 'none',
            rating: 4.8,
            createdAt: '2025-06-05T10:00:00',
            updatedAt: '2025-06-06T16:15:00'
        },
        {
            id: 'JOB005',
            jobNumber: 'TT-2025-005',
            customerPO: 'PO-RELIANCE-2025-0123',
            description: 'Chemical tanker transport',
            type: 'vendor',
            status: 'assigned',
            priority: 'urgent',
            customer: {
                id: 'CUST005',
                name: 'Reliance Industries',
                contact: 'Vikash Gupta',
                phone: '+91-9876543214',
                email: 'vikash.gupta@ril.com',
                address: 'Reliance Corporate Park, Navi Mumbai - 400701'
            },
            vendor: {
                id: 'VEND005',
                name: 'Chemical Logistics India',
                contact: 'Rajesh Varma',
                phone: '+91-9876543225',
                type: 'Chemical Transport'
            },
            transporter: {
                id: 'TRANS005',
                name: 'Mahindra Logistics',
                code: 'MLL',
                contact: 'Kiran Patel',
                phone: '+91-9876543234',
                rating: 4.9,
                totalJobs: 789,
                onTimeDelivery: 97.5
            },
            container: {
                id: 'CONT005',
                number: 'TANK-567890-1',
                type: 'tanker',
                owner: 'Chemical Containers Ltd',
                tare: 5200,
                maxWeight: 35000,
                status: 'Available',
                location: 'Mumbai Chemical Hub',
                freeTime: 48,
                usedTime: 2,
                detentionRate: 1200,
                trackingEnabled: true,
                temperature: 20,
                humidity: 45
            },
            legs: [
                {
                    id: 1,
                    type: 'pickup',
                    origin: 'Chemical Plant, Vadodara - 390020',
                    destination: 'Reliance Refinery, Jamnagar - 361140',
                    distance: 180,
                    estimatedHours: 4.5,
                    actualHours: null,
                    status: 'assigned',
                    progress: 0,
                    journeyDate: '2025-06-08',
                    journeyTime: '06:00'
                }
            ],
            route: {
                totalDistance: 180,
                estimatedDuration: 4.5,
                actualDuration: null,
                currentLeg: 1,
                efficiency: null
            },
            tracking: {
                gpsEnabled: true,
                temperatureMonitoring: true,
                photoUpdates: true,
                realTimeAlerts: true,
                geofencing: true,
                provider: 'ChemTrack'
            },
            alerts: [],
            detentionRisk: 'low',
            rating: null,
            createdAt: '2025-06-07T12:00:00',
            updatedAt: '2025-06-07T12:00:00'
        }
    ];

    // Job status and type configurations
    const JOB_STATUSES = {
        ASSIGNED: { value: 'assigned', label: 'Assigned', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
        LOADING: { value: 'loading', label: 'Loading', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
        IN_TRANSIT: { value: 'in_transit', label: 'In Transit', color: 'purple', bgColor: 'bg-purple-100', textColor: 'text-purple-800' },
        DELAYED: { value: 'delayed', label: 'Delayed', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' },
        COMPLETED: { value: 'completed', label: 'Completed', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' },
        CANCELLED: { value: 'cancelled', label: 'Cancelled', color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800' }
    };

    const JOB_TYPES = {
        IMPORT: { value: 'import', label: 'Import (Port to Warehouse)', icon: Ship },
        EXPORT: { value: 'export', label: 'Export (Warehouse to Port)', icon: Factory },
        DOMESTIC: { value: 'domestic', label: 'Domestic (Warehouse to Customer)', icon: Truck },
        VENDOR: { value: 'vendor', label: 'Vendor (Vendor to Warehouse)', icon: Building2 }
    };

    // Initialize jobs data
    useEffect(() => {
        setLoading(true);
        setTimeout(() => {
            setJobs(mockJobs);
            setFilteredJobs(mockJobs);
            setLoading(false);
        }, 1000);
    }, []);

    // Filter and search logic
    useEffect(() => {
        let filtered = [...jobs];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(job =>
                job.jobNumber.toLowerCase().includes(searchTerm) ||
                job.customerPO.toLowerCase().includes(searchTerm) ||
                job.container.number.toLowerCase().includes(searchTerm) ||
                job.customer.name.toLowerCase().includes(searchTerm) ||
                job.transporter.name.toLowerCase().includes(searchTerm) ||
                job.legs.some(leg =>
                    leg.vehicle?.number.toLowerCase().includes(searchTerm) ||
                    leg.driver?.name.toLowerCase().includes(searchTerm)
                )
            );
        }

        Object.entries(filters).forEach(([key, value]) => {
            if (value && key !== 'search' && key !== 'dateFrom' && key !== 'dateTo' && key !== 'customer' && key !== 'transporter' && key !== 'container' && key !== 'vehicle') {
                filtered = filtered.filter(job => job[key] === value);
            }
        });

        if (filters.customer) {
            filtered = filtered.filter(job =>
                job.customer.name.toLowerCase().includes(filters.customer.toLowerCase())
            );
        }

        if (filters.transporter) {
            filtered = filtered.filter(job =>
                job.transporter.name.toLowerCase().includes(filters.transporter.toLowerCase())
            );
        }

        if (filters.container) {
            filtered = filtered.filter(job =>
                job.container.number.toLowerCase().includes(filters.container.toLowerCase())
            );
        }

        if (filters.vehicle) {
            filtered = filtered.filter(job =>
                job.legs.some(leg =>
                    leg.vehicle?.number.toLowerCase().includes(filters.vehicle.toLowerCase())
                )
            );
        }

        if (filters.dateFrom) {
            filtered = filtered.filter(job =>
                new Date(job.createdAt) >= new Date(filters.dateFrom)
            );
        }

        if (filters.dateTo) {
            filtered = filtered.filter(job =>
                new Date(job.createdAt) <= new Date(filters.dateTo)
            );
        }

        if (filters.trackingStatus) {
            filtered = filtered.filter(job =>
                job.tracking.gpsEnabled === (filters.trackingStatus === 'enabled')
            );
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (sortField === 'createdAt' || sortField === 'updatedAt') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setFilteredJobs(filtered);
        setCurrentPage(1);
    }, [jobs, filters, sortField, sortDirection]);

    // Pagination logic
    const paginatedJobs = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredJobs.slice(startIndex, startIndex + pageSize);
    }, [filteredJobs, currentPage, pageSize]);

    // Total pages calculation
    const totalPages = Math.ceil(filteredJobs.length / pageSize);

    // Event handlers
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSelectJob = (jobId) => {
        setSelectedJobs(prev =>
            prev.includes(jobId)
                ? prev.filter(id => id !== jobId)
                : [...prev, jobId]
        );
    };

    const handleSelectAll = () => {
        if (selectedJobs.length === paginatedJobs.length) {
            setSelectedJobs([]);
        } else {
            setSelectedJobs(paginatedJobs.map(job => job.id));
        }
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            status: '',
            type: '',
            priority: '',
            customer: '',
            transporter: '',
            container: '',
            vehicle: '',
            dateFrom: '',
            dateTo: '',
            detentionRisk: '',
            trackingStatus: ''
        });
    };

    const exportData = () => {
        const csvContent = "data:text/csv;charset=utf-8," +
            "Job Number,Container,Type,Status,Customer,Transporter,Route,Driver,Vehicle,ETA,Cost,Detention Risk\n" +
            filteredJobs.map(job =>
                `${job.jobNumber},${job.container.number},${job.type},${job.status},"${job.customer.name}","${job.transporter.name}","${job.legs.map(leg => `${leg.origin} → ${leg.destination}`).join(' → ')}","${job.legs[0]?.driver?.name || 'N/A'}","${job.legs[0]?.vehicle?.number || 'N/A'}",${job.timeline?.estimatedDelivery || 'N/A'},${job.pricing?.actualCost || job.pricing?.estimatedCost || 'N/A'},${job.detentionRisk}`).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "jobs-export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Utility functions
    const getStatusConfig = (status) => {
        const config = JOB_STATUSES[status.toUpperCase()];
        return config || { color: 'gray', label: status, bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
    };

    const getPriorityConfig = (priority) => {
        const configs = {
            urgent: { color: 'red', label: 'Urgent', icon: AlertTriangle },
            high: { color: 'red', label: 'High Priority', icon: Flag },
            medium: { color: 'yellow', label: 'Medium', icon: Star },
            low: { color: 'green', label: 'Low', icon: CheckCircle }
        };
        return configs[priority] || configs.medium;
    };

    const getDetentionRiskConfig = (risk) => {
        const configs = {
            high: { color: 'red', label: 'High Risk', bgColor: 'bg-red-100', textColor: 'text-red-800' },
            medium: { color: 'yellow', label: 'Medium Risk', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
            low: { color: 'green', label: 'Low Risk', bgColor: 'bg-green-100', textColor: 'text-green-800' },
            none: { color: 'gray', label: 'No Risk', bgColor: 'bg-gray-100', textColor: 'text-gray-800' }
        };
        return configs[risk] || configs.none;
    };

    // UI Components matching your Create Jobs Page
    const Card = ({ children, className = '', onClick }) => (
        <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`} onClick={onClick}>
            {children}
        </div>
    );

    const Button = ({ children, variant = 'primary', size = 'md', className = '', loading, onClick, ...props }) => {
        const baseClasses = 'font-medium focus:outline-none focus:ring-2 transition-colors flex items-center justify-center';
        const variants = {
            primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
            secondary: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
            outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
            ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500',
            danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500'
        };
        const sizes = {
            sm: 'px-3 py-1.5 text-sm rounded',
            md: 'px-4 py-2 rounded-lg',
            lg: 'px-6 py-3 text-lg rounded-lg'
        };

        return (
            <button
                className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
                onClick={onClick}
                disabled={loading}
                {...props}
            >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                {children}
            </button>
        );
    };

    const Badge = ({ children, variant = 'default', size = 'md', className = '' }) => {
        const variants = {
            default: 'bg-gray-100 text-gray-800',
            blue: 'bg-blue-100 text-blue-800',
            green: 'bg-green-100 text-green-800',
            yellow: 'bg-yellow-100 text-yellow-800',
            red: 'bg-red-100 text-red-800',
            purple: 'bg-purple-100 text-purple-800',
            danger: 'bg-red-100 text-red-800'
        };
        const sizes = {
            sm: 'px-2 py-1 text-xs',
            md: 'px-2.5 py-0.5 text-sm'
        };

        return (
            <span className={`inline-flex items-center rounded-full font-medium ${variants[variant]} ${sizes[size]} ${className}`}>
                {children}
            </span>
        );
    };

    const Input = ({ label, icon: Icon, className = '', ...props }) => (
        <div className="space-y-1">
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
            <div className="relative">
                {Icon && <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />}
                <input
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${Icon ? 'pl-10' : ''} ${className}`}
                    {...props}
                />
            </div>
        </div>
    );

    const Select = ({ label, options = [], className = '', ...props }) => (
        <div className="space-y-1">
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
            <select
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${className}`}
                {...props}
            >
                <option value="">All {label}</option>
                {options.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 mx-auto space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center">
                    {/* <Button variant="ghost" onClick={() => window.history.back()} className="mr-4">
                        <ArrowLeft className="w-4 h-4" />
                    </Button> */}
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                            <Package className="w-8 h-8 mr-3 text-blue-600" />
                            Jobs Management
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Manage and track all your logistics jobs with comprehensive multi-leg routing and real-time monitoring
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-4 lg:mt-0">
                    <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                        <Filter className="w-4 h-4 mr-2" />
                        Filters {Object.values(filters).filter(Boolean).length > 0 && `(${Object.values(filters).filter(Boolean).length})`}
                    </Button>

                    <Button variant="outline" onClick={exportData}>
                        <Download className="w-4 h-4 mr-2" />
                        Export
                    </Button>

                    <Button variant="outline" onClick={() => window.location.reload()}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>

                    <Button variant="secondary" onClick={() => window.location.href = '/jobs/create'}>
                        <Plus className="w-4 h-4 mr-2" />
                        New Job
                    </Button>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                {Object.values(JOB_STATUSES).map((status) => {
                    const count = filteredJobs.filter(job => job.status === status.value).length;
                    return (
                        <Card key={status.value} className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() => handleFilterChange('status', status.value)}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                                    <p className="text-sm text-gray-600">{status.label}</p>
                                </div>
                                <div className={`w-10 h-10 rounded-lg ${status.bgColor} flex items-center justify-center`}>
                                    <Activity className={`w-5 h-5 ${status.textColor}`} />
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {/* Advanced Filters */}
            {showFilters && (
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Filter className="w-5 h-5 mr-2 text-blue-600" />
                            Advanced Filters
                        </h3>
                        <Button variant="outline" onClick={clearFilters}>
                            Clear All Filters
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <Input
                            label="Search"
                            placeholder="Job number, container, customer..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            icon={Search}
                        />

                        <Select
                            label="Status"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            options={Object.values(JOB_STATUSES).map(status => ({
                                value: status.value,
                                label: status.label
                            }))}
                        />

                        <Select
                            label="Job Type"
                            value={filters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            options={Object.values(JOB_TYPES).map(type => ({
                                value: type.value,
                                label: type.label
                            }))}
                        />

                        <Select
                            label="Priority"
                            value={filters.priority}
                            onChange={(e) => handleFilterChange('priority', e.target.value)}
                            options={[
                                { value: 'urgent', label: 'Urgent' },
                                { value: 'high', label: 'High Priority' },
                                { value: 'medium', label: 'Medium Priority' },
                                { value: 'low', label: 'Low Priority' }
                            ]}
                        />

                        <Input
                            label="Customer"
                            placeholder="Customer name..."
                            value={filters.customer}
                            onChange={(e) => handleFilterChange('customer', e.target.value)}
                            icon={Building2}
                        />

                        <Input
                            label="Transporter"
                            placeholder="Transporter name..."
                            value={filters.transporter}
                            onChange={(e) => handleFilterChange('transporter', e.target.value)}
                            icon={Truck}
                        />

                        <Input
                            label="Container"
                            placeholder="Container number..."
                            value={filters.container}
                            onChange={(e) => handleFilterChange('container', e.target.value)}
                            icon={Container}
                        />

                        <Input
                            label="Vehicle"
                            placeholder="Vehicle number..."
                            value={filters.vehicle}
                            onChange={(e) => handleFilterChange('vehicle', e.target.value)}
                            icon={Truck}
                        />

                        <Input
                            label="Date From"
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                            icon={Calendar}
                        />

                        <Input
                            label="Date To"
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                            icon={Calendar}
                        />

                        <Select
                            label="Detention Risk"
                            value={filters.detentionRisk}
                            onChange={(e) => handleFilterChange('detentionRisk', e.target.value)}
                            options={[
                                { value: 'high', label: 'High Risk' },
                                { value: 'medium', label: 'Medium Risk' },
                                { value: 'low', label: 'Low Risk' },
                                { value: 'none', label: 'No Risk' }
                            ]}
                        />

                        <Select
                            label="Tracking Status"
                            value={filters.trackingStatus}
                            onChange={(e) => handleFilterChange('trackingStatus', e.target.value)}
                            options={[
                                { value: 'enabled', label: 'Tracking Enabled' },
                                { value: 'disabled', label: 'Tracking Disabled' }
                            ]}
                        />
                    </div>
                </Card>
            )}

            {/* Bulk Actions */}
            {selectedJobs.length > 0 && (
                <Card className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                            <span className="text-sm text-gray-600">
                                {selectedJobs.length} job(s) selected
                            </span>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button variant="outline" size="sm">
                                <FileText className="w-4 h-4 mr-2" />
                                Generate Reports
                            </Button>
                            <Button variant="outline" size="sm">
                                <Download className="w-4 h-4 mr-2" />
                                Export Selected
                            </Button>
                            <Button variant="outline" size="sm">
                                <Edit className="w-4 h-4 mr-2" />
                                Bulk Edit
                            </Button>
                            <Button variant="outline" size="sm">
                                <Bell className="w-4 h-4 mr-2" />
                                Send Alerts
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Jobs Table */}
            <Card>
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <Package className="w-5 h-5 mr-2 text-blue-600" />
                                Jobs List ({filteredJobs.length} total)
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredJobs.length)} of {filteredJobs.length} jobs
                            </p>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button variant="outline" size="sm" onClick={() => handleSort('createdAt')}>
                                <ArrowUpDown className="w-4 h-4 mr-2" />
                                Sort by Date
                                {sortField === 'createdAt' && (
                                    sortDirection === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <input
                                        type="checkbox"
                                        checked={selectedJobs.length === paginatedJobs.length && paginatedJobs.length > 0}
                                        onChange={handleSelectAll}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Job Details
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Customer & Transporter
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Container & Route
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    <button
                                        onClick={() => handleSort('status')}
                                        className="flex items-center hover:text-gray-900"
                                    >
                                        Status & Priority
                                        {sortField === 'status' && (
                                            sortDirection === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                                        )}
                                    </button>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Current Leg & Progress
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tracking & Monitoring
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Timeline
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedJobs.map((job, jobIndex) => {
                                const statusConfig = getStatusConfig(job.status);
                                const priorityConfig = getPriorityConfig(job.priority);
                                const PriorityIcon = priorityConfig.icon;
                                const detentionConfig = getDetentionRiskConfig(job.detentionRisk);
                                const currentLeg = job.legs[job.route.currentLeg - 1] || job.legs[0];
                                const TypeIcon = JOB_TYPES[job.type.toUpperCase()]?.icon || Package;

                                return (
                                    <tr key={job.id} className="hover:bg-gray-50">
                                        {/* Select Checkbox */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <input
                                                type="checkbox"
                                                checked={selectedJobs.includes(job.id)}
                                                onChange={() => handleSelectJob(job.id)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                            />
                                        </td>

                                        {/* Job Details */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                    <TypeIcon className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div className="ml-3">
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => window.location.href = `/jobs/${job.id}`}
                                                            className="font-medium text-blue-600 hover:text-blue-800"
                                                        >
                                                            {job.jobNumber}
                                                        </button>
                                                        {job.alerts.length > 0 && (
                                                            <Badge variant="danger" size="sm" className="ml-2">
                                                                {job.alerts.length} alerts
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-gray-500">{JOB_TYPES[job.type.toUpperCase()]?.label}</p>
                                                    <p className="text-xs text-gray-400">{job.customerPO}</p>
                                                    {job.rating && (
                                                        <div className="flex items-center mt-1">
                                                            <Star className="w-3 h-3 text-yellow-400 mr-1" />
                                                            <span className="text-xs text-gray-500">{job.rating}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Customer & Transporter */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <p className="font-medium text-gray-900">{job.customer.name}</p>
                                                <p className="text-sm text-gray-600">{job.customer.contact}</p>
                                                <div className="mt-2 pt-2 border-t border-gray-100">
                                                    <p className="text-sm text-gray-600">{job.transporter.name} ({job.transporter.code})</p>
                                                    <div className="flex items-center mt-1">
                                                        <Star className="w-3 h-3 text-yellow-400 mr-1" />
                                                        <span className="text-xs text-gray-500">{job.transporter.rating}</span>
                                                        <span className="text-xs text-gray-400 ml-2">
                                                            {job.transporter.onTimeDelivery}% OTD
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Container & Route */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="flex items-center mb-1">
                                                    <Container className="w-4 h-4 mr-1 text-gray-400" />
                                                    <span className="font-medium text-gray-900">{job.container.number}</span>
                                                </div>
                                                <p className="text-sm text-gray-600">{job.container.type.toUpperCase()} - {job.container.owner}</p>
                                                <div className="mt-2">
                                                    <div className="flex items-center text-xs text-gray-500">
                                                        <MapPin className="w-3 h-3 mr-1 text-green-500" />
                                                        <span className="truncate max-w-32">{job.legs[0]?.origin.split(',')[0]}</span>
                                                    </div>
                                                    <div className="flex items-center text-xs text-gray-500">
                                                        <Target className="w-3 h-3 mr-1 text-red-500" />
                                                        <span className="truncate max-w-32">{job.legs[job.legs.length - 1]?.destination.split(',')[0]}</span>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">{job.route.totalDistance}km • {job.legs.length} legs</p>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Detention: {job.container.usedTime}h / {job.container.freeTime}h
                                                </div>
                                            </div>
                                        </td>

                                        {/* Status & Priority */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-2">
                                                <Badge
                                                    variant={statusConfig.color}
                                                    className={`${statusConfig.bgColor} ${statusConfig.textColor}`}
                                                >
                                                    {statusConfig.label}
                                                </Badge>
                                                <div className="flex items-center">
                                                    <Badge variant={priorityConfig.color} size="sm">
                                                        <PriorityIcon className="w-3 h-3 mr-1" />
                                                        {priorityConfig.label}
                                                    </Badge>
                                                </div>
                                                {job.detentionRisk !== 'none' && (
                                                    <Badge size="sm" className={`${detentionConfig.bgColor} ${detentionConfig.textColor}`}>
                                                        {detentionConfig.label}
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>

                                        {/* Current Leg & Progress */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="flex items-center mb-2">
                                                    <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                                                        <div
                                                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                                            style={{ width: `${currentLeg?.progress || 0}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-sm text-gray-600">{currentLeg?.progress || 0}%</span>
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    <div className="flex items-center">
                                                        <Map className="w-3 h-3 mr-1" />
                                                        <span>Leg {job.route.currentLeg} of {job.legs.length}</span>
                                                    </div>
                                                    {currentLeg?.vehicle && (
                                                        <div className="flex items-center mt-1">
                                                            <Truck className="w-3 h-3 mr-1" />
                                                            <span>{currentLeg.vehicle.number}</span>
                                                        </div>
                                                    )}
                                                    {currentLeg?.driver && (
                                                        <div className="flex items-center mt-1">
                                                            <User className="w-3 h-3 mr-1" />
                                                            <span>{currentLeg.driver.name}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {job.tracking.gpsEnabled && (
                                                    <div className="flex items-center mt-2">
                                                        <Navigation className="w-3 h-3 mr-1 text-green-500" />
                                                        <span className="text-xs text-green-600">Live Tracking</span>
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        {/* Tracking & Monitoring */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-2">
                                                <div className="flex items-center space-x-3">
                                                    {job.tracking.gpsEnabled && (
                                                        <div className="flex items-center">
                                                            <Navigation className="w-3 h-3 text-green-500 mr-1" />
                                                            <span className="text-xs text-green-600">GPS</span>
                                                        </div>
                                                    )}
                                                    {job.tracking.temperatureMonitoring && (
                                                        <div className="flex items-center">
                                                            <Thermometer className="w-3 h-3 text-blue-500 mr-1" />
                                                            <span className="text-xs text-blue-600">{job.container.temperature}°C</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    {job.tracking.photoUpdates && (
                                                        <div className="flex items-center">
                                                            <Camera className="w-3 h-3 text-purple-500 mr-1" />
                                                            <span className="text-xs text-purple-600">Photos</span>
                                                        </div>
                                                    )}
                                                    {job.tracking.realTimeAlerts && (
                                                        <div className="flex items-center">
                                                            <Bell className="w-3 h-3 text-orange-500 mr-1" />
                                                            <span className="text-xs text-orange-600">Alerts</span>
                                                        </div>
                                                    )}
                                                    {job.tracking.geofencing && (
                                                        <div className="flex items-center">
                                                            <Shield className="w-3 h-3 text-indigo-500 mr-1" />
                                                            <span className="text-xs text-indigo-600">Geofence</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Provider: {job.tracking.provider}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Timeline */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-xs text-gray-500 mb-2">
                                                    <Clock className="w-3 h-3 inline mr-1" />
                                                    ETA: {job.timeline?.estimatedDelivery ?
                                                        new Date(job.timeline.estimatedDelivery).toLocaleDateString() :
                                                        'TBD'}
                                                </div>
                                                <div className="text-xs text-gray-500 mb-2">
                                                    {job.timeline?.estimatedDelivery ?
                                                        new Date(job.timeline.estimatedDelivery).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                                                        'Time TBD'}
                                                </div>
                                                {/* <div className="text-right">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        ₹{(job.pricing?.actualCost || job.pricing?.estimatedCost || 0).toLocaleString()}
                                                    </p>
                                                    {job.pricing?.estimatedCost && (
                                                        <p className="text-xs text-gray-500">
                                                            Est: ₹{job.pricing.estimatedCost.toLocaleString()}
                                                        </p>
                                                    )}
                                                    {job.pricing?.actualCost && job.pricing?.estimatedCost && (
                                                        <div className={`text-xs ${job.pricing.actualCost <= job.pricing.estimatedCost ? 'text-green-600' : 'text-red-600'}`}>
                                                            {job.pricing.actualCost <= job.pricing.estimatedCost ? '↓' : '↑'}
                                                            {((job.pricing.actualCost - job.pricing.estimatedCost) / job.pricing.estimatedCost * 100).toFixed(1)}%
                                                        </div>
                                                    )}
                                                </div> */}
                                                <div className="text-xs text-gray-500 mt-2">
                                                    <Timer className="w-3 h-3 inline mr-1" />
                                                    {job.route.estimatedDuration}h total
                                                </div>
                                            </div>
                                        </td>

                                        {/* Actions */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-1">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => indow.location.href = `/jobs/${job.id}`}
                                                    title="View Details"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {job.tracking.gpsEnabled && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => alert(`Live tracking for ${job.jobNumber}`)}
                                                        title="Live Tracking"
                                                    >
                                                        <Map className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => alert(`Edit job ${job.jobNumber}`)}
                                                    title="Edit Job"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700"
                                                    title="More Actions"
                                                >
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Empty State */}
                {paginatedJobs.length === 0 && (
                    <div className="text-center py-12">
                        <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                        <p className="text-gray-600 mb-4">No jobs match your current filter criteria.</p>
                        <Button variant="outline" onClick={clearFilters}>
                            Clear Filters
                        </Button>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between items-center">
                            <div className="flex items-center">
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                                    <span className="font-medium">{Math.min(currentPage * pageSize, filteredJobs.length)}</span> of{' '}
                                    <span className="font-medium">{filteredJobs.length}</span> results
                                </p>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                    className="ml-4 text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                    <option value={10}>10 per page</option>
                                    <option value={20}>20 per page</option>
                                    <option value={50}>50 per page</option>
                                    <option value={100}>100 per page</option>
                                </select>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>

                                <div className="flex space-x-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        const page = Math.max(1, currentPage - 2) + i;
                                        if (page > totalPages) return null;

                                        return (
                                            <Button
                                                key={page}
                                                variant={currentPage === page ? 'primary' : 'outline'}
                                                size="sm"
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </Button>
                                        );
                                    })}
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                        Financial Summary
                    </h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Total Revenue</span>
                            <span className="font-medium">
                                ₹{filteredJobs.reduce((sum, job) => sum + (job.pricing?.actualCost || job.pricing?.estimatedCost || 0), 0).toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Average Job Value</span>
                            <span className="font-medium">
                                ₹{(filteredJobs.reduce((sum, job) => sum + (job.pricing?.actualCost || job.pricing?.estimatedCost || 0), 0) / Math.max(filteredJobs.length, 1)).toLocaleString()}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Jobs with Cost Overrun</span>
                            <span className="font-medium text-red-600">
                                {filteredJobs.filter(job =>
                                    job.pricing?.actualCost && job.pricing?.estimatedCost &&
                                    job.pricing.actualCost > job.pricing.estimatedCost
                                ).length}
                            </span>
                        </div>
                    </div>
                </Card> */}

                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Activity className="w-5 h-5 mr-2 text-blue-600" />
                        Performance Metrics
                    </h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600">On-Time Delivery Rate</span>
                            <span className="font-medium text-green-600">
                                {(filteredJobs.filter(job => job.status === 'completed').length / Math.max(filteredJobs.length, 1) * 100).toFixed(1)}%
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Jobs with Delays</span>
                            <span className="font-medium text-red-600">
                                {filteredJobs.filter(job => job.status === 'delayed').length}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Average Rating</span>
                            <span className="font-medium">
                                {(filteredJobs.filter(job => job.rating).reduce((sum, job) => sum + job.rating, 0) /
                                    Math.max(filteredJobs.filter(job => job.rating).length, 1)).toFixed(1)}
                            </span>
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                        Risk Management
                    </h3>
                    <div className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-gray-600">High Detention Risk</span>
                            <span className="font-medium text-red-600">
                                {filteredJobs.filter(job => job.detentionRisk === 'high').length}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Active Alerts</span>
                            <span className="font-medium text-orange-600">
                                {filteredJobs.reduce((sum, job) => sum + job.alerts.length, 0)}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">GPS Tracking Enabled</span>
                            <span className="font-medium text-green-600">
                                {filteredJobs.filter(job => job.tracking.gpsEnabled).length}
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default JobsPage;